/**
 * SupersedeBatchConfirmDialog (2026-07-08)
 *
 * Coordinator confirm shown when a re-batch would invalidate stickers that are
 * already printed AND physically stuck on shirts. Re-batching mints new units
 * (SUPERSEDED-ing the old ones) — someone then has to reprint + re-stick those N
 * stickers. Surface that cost before it happens; the operator confirms to proceed
 * with `confirmSupersede: true`.
 */
import { useTranslation } from 'react-i18next';
import { AlertTriangle } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

interface SupersedeBatchConfirmDialogProps {
  open: boolean;
  count: number;
  onConfirm: () => void;
  onCancel: () => void;
  isPending?: boolean;
}

export function SupersedeBatchConfirmDialog({
  open,
  count,
  onConfirm,
  onCancel,
  isPending = false,
}: SupersedeBatchConfirmDialogProps) {
  const { t } = useTranslation('production');

  return (
    <AlertDialog open={open} onOpenChange={(o) => !o && onCancel()}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2 text-amber-600 dark:text-amber-500">
            <AlertTriangle className="h-5 w-5" aria-hidden="true" />
            {t('batch.supersede.title')}
          </AlertDialogTitle>
          <AlertDialogDescription className="space-y-2 pt-2 text-foreground">
            <span className="block">{t('batch.supersede.line1', { count })}</span>
            <span className="block">{t('batch.supersede.line2', { count })}</span>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isPending}>
            {t('batch.supersede.cancel')}
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={onConfirm}
            disabled={isPending}
            className="bg-amber-600 hover:bg-amber-700 text-white focus-visible:ring-amber-600"
          >
            {t('batch.supersede.confirm')}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
